import videojs from 'video.js';
import localizationDE from 'video.js/dist/lang/de.json';
import additionalTranslationDE from './lang/de.json';
// import { pluginAriaLiveStatus } from './plugins/aria-live-status/aria-live-status';
import { pluginAudioDescription } from './plugins/audio-description/audio-description';
import { pluginA11yTooltips } from './plugins/a11y-tooltips/a11y-tooltips';
import { pluginAddLocalizations } from './plugins/add-localizations/add-localizations';
import { pluginQualitySelector } from './plugins/quality-selector/quality-selector';
import { pluginSignLanguage } from './plugins/sign-language/sign-language';
import { pluginSocialShare } from './plugins/social-share/social-share';
import { pluginDownloadOption } from './plugins/download-option/download-option';
import 'video.js/dist/video-js.css';
import './style/player.scss';

const generateRandomNumber = seed => {
  seed = seed || 1;
  return Math.floor(
    Math.random() * Math.floor(Math.random() * Date.now() * seed)
  );
};

class Player {
  constructor(params) {
    this.player = params.player;
    this.playerId = generateRandomNumber(this.player.offsetTop);
    this.mainSrc = this.player.querySelector('video, audio') || this.player;
    this.mediumType = this.mainSrc.nodeName.toLowerCase();
    this.defaultConfig = {
      fluid: true,
      language: document.documentElement.lang
        ? document.documentElement.lang.split('-')[0]
        : 'de',
      aspectRatio: this.mediumType === 'video' ? '16:9' : undefined,
      audioOnlyMode: this.mediumType === 'audio',
      controls: true,
      playbackRates: [0.5, 1, 1.5],
      controlBar: {
        pictureInPictureToggle: false,
        pluginOrder: ['audioDescription', 'signLanguage',  'downloadOption', 'qualitySelector', 'socialShare'],
      },
      restoreEl: true,
    };
    this.config = Object.assign({}, this.defaultConfig, params.config || {});
    this.playerInstance = undefined;
    this.playerInstanceWasDestroyed = false;
  }

  init = () => {
    window.videojs = window.videojs || videojs;
    if (this.playerInstanceWasDestroyed) {
      this.playerInstanceWasDestroyed = false;
      // If this.mainSrc was replaced by a new DOM node as a result of video.js API .dispose() we must find it again.
      this.mainSrc =
        // If this.player was a wrapper for this.mainSrc and is still available:
        (this.player && this.player.querySelector('video, audio')) ||
        // If the original this.player equaled this.mainSrc retrieve new this.mainSrc via this.playerId:
        document.querySelector(`[data-player-id='${this.playerId}']`);
    }
    this.setupPlayer(window.videojs);
  };

  destroy = () => {
    !this.playerInstanceWasDestroyed &&
      this.playerInstance &&
      this.playerInstance.dispose();
    this.playerInstanceWasDestroyed = true;
  };

  setupPlayer(videojs) {
    this.player.setAttribute('data-player-id', this.playerId);

    if (this.player && this.player.dataset && this.player.dataset.socialShare) {
      this.mainSrc.dataset.socialShare = this.player.dataset.socialShare;
    }

    if (
      this.player &&
      this.player.dataset &&
      this.player.dataset.downloadOption
    ) {
      this.mainSrc.dataset.downloadOption = this.player.dataset.downloadOption;
    }

    if (
      this.player &&
      this.player.dataset &&
      this.player.dataset.signLanguage
    ) {
      this.mainSrc.dataset.signLanguage = this.player.dataset.signLanguage;
    }

    if (
      this.player &&
      this.player.dataset &&
      this.player.dataset.cookieBlockYoutube
    ) {
      import('@3pc/player-videojs-fix-youtube-tech').then(() => {
        this.mainSrc.dataset.setup = this.player.dataset.cookieBlockYoutube;
        this.initCore(videojs);
      });
      return;
    }

    if (
      this.player &&
      this.player.dataset &&
      this.player.dataset.cookieBlockVimeo
    ) {
      import('@3pc/player-videojs-fix-vimeo-tech').then(() => {
        this.mainSrc.dataset.setup = this.player.dataset.cookieBlockVimeo;
        this.initCore(videojs);
      });
      return;
    }

    this.initCore(videojs);
  }

  initCore(videojs) {
    this.mainSrc.classList.add('video-js');
    this.prepareVideoJs(videojs);
    const player = videojs(this.mainSrc, this.config);
    this.playerInstance = player;
    this.playerInstance.usesExternalTech = !!(this.player.dataset.cookieBlockYoutube || this.player.dataset.cookieBlockVimeo);
    this.launchPlugins(player);

    // If .destroy() was triggered earlier than the completion of the player instantiation.
    if (this.playerInstanceWasDestroyed) this.destroy();
  }

  prepareVideoJs(videojs) {
    this.addLocalization(videojs);
    this.registerPlugins(videojs);
  }

  addLocalization(videojs) {
    const lang = this.config.language;
    if (lang === 'de') {
      videojs.addLanguage(this.config.language, localizationDE);
      videojs.addLanguage(this.config.language, additionalTranslationDE);
    }
  }

  registerPlugins(videojs) {
    // pluginAriaLiveStatus(videojs);
    !videojs.getPlugin('audioDescription') && pluginAudioDescription(videojs);
    !videojs.getPlugin('addLocalizations') && pluginAddLocalizations(videojs);
    !videojs.getPlugin('a11yTooltips') && pluginA11yTooltips(videojs);
    !videojs.getPlugin('qualitySelector') && pluginQualitySelector(videojs);
    !videojs.getPlugin('signLanguage') && pluginSignLanguage(videojs);
    !videojs.getPlugin('socialShare') && pluginSocialShare(videojs);
    !videojs.getPlugin('downloadOption') && pluginDownloadOption(videojs);
  }

  launchPlugins(player) {
    // videojs.getPlugin('ariaLiveStatus')(player);
    this.config.controlBar.pluginOrder.forEach(pluginName => {
      videojs.getPlugin(pluginName)(player, this.config);
    });
    videojs.getPlugin('addLocalizations')(player);
    // Render a11yTooltips last to ensure DOM elements added via plugins get a tooltip treatment.
    // Also a11yTooltips has to be executed after 'addLocalizations' in order to turn added localizations to tooltips.
    videojs.getPlugin('a11yTooltips')(player);
  }
}

export default Player;
export { Player };
